import { Component, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DEFAULT_BANK, OptionView, Question, quizListPath, toOptionView } from '../../../models/quiz';
import { MotionService } from '../../../motion/motion.service';
import { QuizResult, QuizResultsService } from '../../../services/quiz-results.service';

type ReviewStatus = 'correct' | 'wrong' | 'skipped';
type ReviewFilter = 'all' | 'mistakes';

interface ReviewItem {
  number: number;
  question: Question;
  options: OptionView[];
  answer: number | null;
  status: ReviewStatus;
}

@Component({
  selector: 'app-quiz-results',
  templateUrl: './quiz-results.component.html',
  styleUrls: ['./quiz-results.component.css'],
})
export class QuizResultsComponent implements OnInit {
  result: QuizResult | null = null;
  items: ReviewItem[] = [];
  filter: ReviewFilter = 'all';
  currentLang: string = 'am';

  private readonly motion = inject(MotionService);
  /** The result's entrance effects play, except when the page is re-created by a language switch. */
  readonly intro = !this.motion.recentLanguageSwitch();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private quizResultsService: QuizResultsService,
    private translate: TranslateService
  ) {
    this.route.params.subscribe((params) => {
      const lang = params['lang'];
      if (lang) {
        this.currentLang = lang;
        this.translate.use(lang);
        try {
          localStorage.setItem('selectedLanguage', lang);
        } catch {
          // Storage blocked - the language still comes from the URL.
        }
      } else {
        this.currentLang = storedLanguage();
        this.translate.use(this.currentLang);
      }
    });
  }

  ngOnInit(): void {
    this.result = this.quizResultsService.getResult();
    if (!this.result) {
      return;
    }
    const { questions, answers } = this.result;
    this.items = questions.map((question, i) => {
      const answer = answers[i] ?? null;
      const status: ReviewStatus =
        answer === null ? 'skipped' : answer === question.correctAnswerIndex - 1 ? 'correct' : 'wrong';
      return { number: i + 1, question, options: question.options.map(toOptionView), answer, status };
    });
    // Students mostly come here to learn from their mistakes - start there when there are any.
    if (this.mistakeCount > 0) {
      this.filter = 'mistakes';
    }
  }

  get visibleItems() {
    return this.filter === 'all' ? this.items : this.items.filter((item) => item.status !== 'correct');
  }

  get correctCount() {
    return this.count('correct');
  }

  get wrongCount() {
    return this.count('wrong');
  }

  get skippedCount() {
    return this.count('skipped');
  }

  get mistakeCount() {
    return this.wrongCount + this.skippedCount;
  }

  get duration() {
    const seconds = this.result?.durationSeconds ?? 0;
    return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
  }

  /** Test list of the bank the result belongs to (also the base of the retry link). */
  get listLink() {
    return quizListPath(this.currentLang, this.result?.bank ?? DEFAULT_BANK);
  }

  get retryQueryParams() {
    return this.result?.mode === 'practice' ? { mode: 'practice' } : {};
  }

  setFilter(filter: ReviewFilter) {
    if (filter === this.filter) return;
    this.filter = filter;
    // Cards were added or removed above the ones still waiting to reveal: re-measure their triggers.
    this.motion.requestRefresh();
  }

  goBackToQuizList() {
    this.router.navigate([this.listLink]);
  }

  trackByNumber(_: number, item: ReviewItem) {
    return item.number;
  }

  private count(status: ReviewStatus) {
    return this.items.filter((item) => item.status === status).length;
  }
}

function storedLanguage() {
  try {
    return localStorage.getItem('selectedLanguage') || 'am';
  } catch {
    return 'am';
  }
}
